/* eslint-disable tailwindcss/no-custom-classname */
import type { FlexShrinkGrowNumber, Spacing } from '@frontend/constants';
import type { BorderRadius } from '@frontend/constants/theme/borderRadius';
import { colorAliases, type ColorName } from '@frontend/constants/theme/colors';
import type { StrictOmit } from '@frontend/duck-tape';
import { changeCase, omit } from '@frontend/duck-tape';
import type { WebTestId } from '@frontend/test-utils';
import type { ClassName } from '@frontend/web-utils';
import { concatClasses, mergeAndConcatClasses, safeCss } from '@frontend/web-utils';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import type { DivProps } from 'react-html-props';
import type { ClickableProps } from '../Clickable';

export type StackStyleProps = {
  // Alignment properties
  alignContent?: 'around' | 'between' | 'center' | 'end' | 'start' | 'stretch';
  alignItems?: 'baseline' | 'center' | 'end' | 'start' | 'stretch';
  alignSelf?: 'auto' | 'baseline' | 'center' | 'end' | 'start' | 'stretch';
  // Background color
  bg?: ColorName;
  // Border properties
  borderBottom?: boolean;
  borderColor?: ColorName;
  borderLeft?: boolean;
  borderRight?: boolean;

  borderTop?: boolean;

  borderWidth?: number;
  /**
   * @description Shorthand for `alignItems: 'center'` and `justifyContent: 'center'`. Will override any `alignItems` or `justifyContent` properties.
   */
  centered?: boolean;
  // Additional props for web
  className?: ClassName;
  // Flex properties
  flex?: 0 | 1;
  flexGrow?: FlexShrinkGrowNumber;
  flexShrink?: FlexShrinkGrowNumber;

  flexWrap?: 'nowrap' | 'wrap' | 'wrapReverse';

  // Gap properties
  gap?: Spacing;

  gapX?: Spacing;
  gapY?: Spacing;
  height?: CSSProperties['height'];
  justifyContent?: 'around' | 'between' | 'center' | 'end' | 'evenly' | 'start';

  // Margin properties
  m?: Spacing;
  mb?: Spacing;
  ml?: Spacing;

  mr?: Spacing;
  mt?: Spacing;
  mx?: Spacing;
  my?: Spacing;
  // Padding properties
  p?: Spacing;
  pb?: Spacing;
  pl?: Spacing;

  pr?: Spacing;
  pt?: Spacing;
  px?: Spacing;
  py?: Spacing;
  // Border radius
  radius?: BorderRadius;
  reversed?: boolean;
  size?: CSSProperties['height'];

  width?: CSSProperties['width'];
};

// Map flex direction to corresponding Tailwind class
export const flexDirectionTwMapping = {
  column: safeCss('flex-col'),
  columnReverse: safeCss('flex-col-reverse'),
  row: safeCss('flex-row'),
  rowReverse: safeCss('flex-row-reverse'),
};

export type FlexDirection = keyof typeof flexDirectionTwMapping;

// Use these props to build our class strings
export const useStackStyles = (props: StackStyleProps, flexDirection: FlexDirection) => {
  const {
    alignContent,
    alignItems,
    alignSelf,
    bg,
    borderBottom,
    borderColor,
    borderLeft,
    borderRight,
    borderTop,
    borderWidth,
    centered,
    className,
    flex,
    flexGrow,
    flexShrink,
    flexWrap,
    gap,
    gapX,
    gapY,
    height,
    justifyContent,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    radius,
    size,
    width,
  } = props;

  const style = useMemo(() => {
    return {
      ...(width !== undefined && { width }),
      ...(height !== undefined && { height }),
      ...(size !== undefined && { height: size, width: size }),
    } satisfies CSSProperties;
  }, [width, height, size]);

  // Calculate all classes based on props without mutations
  const classes = useMemo(() => {
    // Generate classes using concatenation instead of mutation
    return concatClasses(
      // Base classes
      'flex',
      flexDirectionTwMapping[flexDirection],

      // Centered shorthand (overrides alignItems and justifyContent)
      centered ? safeCss('items-center') : null,
      centered ? safeCss('justify-center') : null,

      // Alignment props (only applied if not centered)
      !centered && alignItems ? safeCss(`items-${alignItems}`) : null,
      !centered && justifyContent ? safeCss(`justify-${changeCase(justifyContent, 'kebabCase')}`) : null,

      // Other flex props
      alignContent ? safeCss(`content-${changeCase(alignContent, 'kebabCase')}`) : null,
      alignSelf ? safeCss(`self-${alignSelf}`) : null,
      flexWrap ? safeCss(`flex-${flexWrap === 'nowrap' ? 'nowrap' : changeCase(flexWrap, 'kebabCase')}`) : null,
      flexGrow !== undefined ? safeCss(`grow-${flexGrow}`) : null,
      flexShrink !== undefined ? safeCss(`shrink-${flexShrink}`) : null,
      flex !== undefined ? safeCss(`flex-${flex}`) : null,

      // Spacing
      gap !== undefined ? safeCss(`gap-${gap}`) : null,
      gapX !== undefined ? safeCss(`gap-x-${gapX}`) : null,
      gapY !== undefined ? safeCss(`gap-y-${gapY}`) : null,

      // Margins
      m !== undefined ? safeCss(`m-${m}`) : null,
      mx !== undefined ? safeCss(`mx-${mx}`) : null,
      my !== undefined ? safeCss(`my-${my}`) : null,
      mt !== undefined ? safeCss(`mt-${mt}`) : null,
      mb !== undefined ? safeCss(`mb-${mb}`) : null,
      ml !== undefined ? safeCss(`ml-${ml}`) : null,
      mr !== undefined ? safeCss(`mr-${mr}`) : null,

      // Paddings
      p !== undefined ? safeCss(`p-${p}`) : null,
      px !== undefined ? safeCss(`px-${px}`) : null,
      py !== undefined ? safeCss(`py-${py}`) : null,
      pt !== undefined ? safeCss(`pt-${pt}`) : null,
      pb !== undefined ? safeCss(`pb-${pb}`) : null,
      pl !== undefined ? safeCss(`pl-${pl}`) : null,
      pr !== undefined ? safeCss(`pr-${pr}`) : null,

      // Background color
      bg ? safeCss(`bg-${handleColorClassName(bg)}`) : null,

      // Borders
      borderWidth ? safeCss(`border-${borderWidth}`) : null,
      borderWidth
        ? borderColor
          ? safeCss(`border-${handleColorClassName(borderColor)}`)
          : safeCss('border-primary')
        : null,

      // Individual borders
      borderTop ? safeCss('border-t') : null,
      borderTop
        ? borderColor
          ? safeCss(`border-t-${handleColorClassName(borderColor)}`)
          : safeCss('border-t-primary')
        : null,

      borderRight ? safeCss('border-r') : null,
      borderRight
        ? borderColor
          ? safeCss(`border-r-${handleColorClassName(borderColor)}`)
          : safeCss('border-r-primary')
        : null,

      borderBottom ? safeCss('border-b') : null,
      borderBottom
        ? borderColor
          ? safeCss(`border-b-${handleColorClassName(borderColor)}`)
          : safeCss('border-b-primary')
        : null,

      borderLeft ? safeCss('border-l') : null,
      borderLeft
        ? borderColor
          ? safeCss(`border-l-${handleColorClassName(borderColor)}`)
          : safeCss('border-l-primary')
        : null,

      // Border radius
      radius !== undefined ? safeCss(`rounded-${radius}`) : null,

      // Additional class name
      className,
    );
  }, [
    alignContent,
    alignItems,
    alignSelf,
    bg,
    borderBottom,
    borderColor,
    borderLeft,
    borderRight,
    borderTop,
    borderWidth,
    centered,
    className,
    flex,
    flexDirection,
    flexGrow,
    flexShrink,
    flexWrap,
    gap,
    gapX,
    gapY,
    justifyContent,
    m,
    mb,
    ml,
    mr,
    mt,
    mx,
    my,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    radius,
  ]);

  return { classes, style };
};

// Props when no onPress is provided (View)
type StackViewProps = {
  canFocus?: never;
  disabled?: never;
  enableHoverEffect?: never;
  onClick?: never;
  preventDefault?: never;
};

// Props when onPress is provided (clickable)
type StackClickableProps = {
  canFocus?: boolean;
  disabled?: boolean;
  enableHoverEffect?: boolean;
  onClick?: DivProps['onClick'];
  preventDefault?: boolean;
};

// Base props for Stack components
type StackBaseProps = StackStyleProps & {
  accessibilityLabel?: string;
  testId?: WebTestId;
};
// Union type for all possible props
export type StackProps = DivProps & StackBaseProps & (StackClickableProps | StackViewProps);

// List of props keys that belong to StackStyleProps
export const STACK_STYLE_PROPS = [
  'alignContent',
  'alignItems',
  'alignSelf',
  'bg',
  'borderBottom',
  'borderColor',
  'borderLeft',
  'borderRight',
  'borderTop',
  'borderWidth',
  'centered',
  'className',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'gap',
  'gapX',
  'gapY',
  'justifyContent',
  'm',
  'mb',
  'ml',
  'mr',
  'mt',
  'mx',
  'my',
  'p',
  'pb',
  'pl',
  'pr',
  'pt',
  'px',
  'py',
  'radius',
] as const satisfies (keyof StackStyleProps)[];

// Define type for the stack props keys
export type StackStylePropKey = (typeof STACK_STYLE_PROPS)[number];

// Helper to omit stack style props from a props object
export const omitStackProps = <T extends Record<string, unknown>>(props: T) => {
  return omit(props, [
    ...STACK_STYLE_PROPS,
    'testId',
    'accessibilityLabel',
    'canFocus',
    'enableHoverEffect',
    'disabled',
    'preventDefault',
  ]) satisfies StrictOmit<
    T,
    StackStylePropKey | 'accessibilityLabel' | 'canFocus' | 'disabled' | 'enableHoverEffect' | 'preventDefault' | 'testId'
  >;
};

// Helper to create clickable props with accessibility features
export const createClickableProps = (props: StrictOmit<StackProps, 'style'>, classes: string, style: CSSProperties) => {
  const { accessibilityLabel, canFocus, disabled, enableHoverEffect, onClick } = props;

  return {
    'aria-disabled': disabled,
    'aria-label': accessibilityLabel,
    className: mergeAndConcatClasses(
      classes,
      enableHoverEffect && !!onClick && !disabled ? 'hover-background-effect' : undefined,
      disabled ? 'cursor-not-allowed' : undefined,
    ),
    onClick: disabled ? undefined : onClick,
    role: 'button',
    style,
    tabIndex: onClick && canFocus ? 0 : undefined,
  } satisfies ClickableProps;
};

const handleColorClassName = (color: ColorName) => (color in colorAliases ? color : changeCase(color, 'kebabCase'));
